import { useState, useRef, useEffect, useCallback } from "react";
import {
  UploadCloud,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  XCircle,
  Plus,
  Trash2,
  Play,
  RotateCcw,
  FileText,
  Clock,
  Download,
} from "lucide-react";
import { useStore } from "../store/useStore";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { cn } from "../utils/helpers";
import { localStorageUtils, STORAGE_KEYS } from "../utils/localStorage";
import * as model2Service from "../services/model2Service";

const ANALYTICS_API_BASE_URL = `${model2Service.API_BASE_URL}/api-analytics/v1`;

// --- Types ---
interface UploadFile {
  file: File;
  id: string;
  progress: number;
  status: "pending" | "uploading" | "success" | "error";
  error?: string;
}

interface RecentUpload {
  id: string;
  name: string;
  size: number;
  timestamp: string;
  status: "success" | "error";
  sourceId?: string;
}

interface UploadedFileItem {
  id: string;
  name: string;
  status: model2Service.Source["status"];
  downloadUrl: string | null;
  fileAvailable: boolean;
  created: string;
  size?: number;
}

const getFilenameFromPath = (value: string) => {
  const withoutQuery = value.split("?")[0];
  const segments = withoutQuery.split("/").filter(Boolean);
  const filename = segments[segments.length - 1] || value;
  try {
    return decodeURIComponent(filename);
  } catch {
    return filename;
  }
};

const getSourceDisplayName = (
  source: model2Service.Source,
  fallbackName?: string,
) => {
  if (source.title?.trim()) return source.title.trim();

  const assetPath = source.asset?.file_path || source.asset?.url;
  if (assetPath?.trim()) return getFilenameFromPath(assetPath);

  if (fallbackName?.trim()) return fallbackName.trim();
  return source.id;
};

export default function Upload() {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isUploadingAll, setIsUploadingAll] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileItem[]>([]);
  const [isLoadingUploadedFiles, setIsLoadingUploadedFiles] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isInitializingModel2, setIsInitializingModel2] = useState(false);
  const [deletingSourceId, setDeletingSourceId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addNotification = useStore((s) => s.addNotification);

  // Load recent uploads and initialize Model 2
  const refreshUploadedFiles = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!silent) setIsLoadingUploadedFiles(true);
      try {
        const recentBySourceId = new Map(
          localStorageUtils
            .get<RecentUpload[]>(STORAGE_KEYS.RECENT_UPLOADS, [])
            .filter((u) => u.sourceId)
            .map((u) => [u.sourceId as string, u]),
        );

        const sources = await model2Service.getSources(100, 0);

        const normalized = sources
          .map((source) => {
            const localRecent = recentBySourceId.get(source.id);
            return {
              id: source.id,
              name: getSourceDisplayName(source, localRecent?.name),
              status: source.status,
              downloadUrl: source.asset?.url || source.asset?.file_path || null,
              fileAvailable: !!source.file_available,
              created: source.created,
              size: localRecent?.size,
            };
          })
          .sort((a, b) => (a.created < b.created ? 1 : -1));

        setUploadedFiles(normalized);
      } catch (error) {
        console.error("Failed to refresh uploaded files:", error);
      } finally {
        if (!silent) setIsLoadingUploadedFiles(false);
      }
    },
    [],
  );

  const deleteAnalyticsFileByName = useCallback(async (fileName: string) => {
    const cleanedName = fileName?.trim();
    if (!cleanedName) return;

    const response = await fetch(
      `${ANALYTICS_API_BASE_URL}/files/name/${encodeURIComponent(cleanedName)}`,
      {
        method: "DELETE",
        headers: { Accept: "application/json" },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed analytics delete: ${response.status}`);
    }
  }, []);

  const waitForSourceCompletion = useCallback(
    async (sourceId: string) => {
      const maxAttempts = 40;
      const delayMs = 3000;

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        try {
          const source = await model2Service.getSource(sourceId);

          if (source.status === "completed") {
            await refreshUploadedFiles({ silent: true });
            addNotification("success", `${getSourceDisplayName(source)} is ready`);
            return;
          }

          if (source.status === "error") {
            await refreshUploadedFiles({ silent: true });
            addNotification(
              "error",
              `${getSourceDisplayName(source)} failed during processing`,
            );
            return;
          }

          await refreshUploadedFiles({ silent: true });
        } catch (error) {
          console.warn("Polling source status failed:", error);
        }

        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }

      await refreshUploadedFiles({ silent: true });
      addNotification("info", "File is still processing in the background");
    },
    [addNotification, refreshUploadedFiles],
  );

  const initializeModel2Background = useCallback(async () => {
    try {
      setIsInitializingModel2(true);

      const notebookId = localStorageUtils.get<string | null>(
        STORAGE_KEYS.MODEL2_NOTEBOOK_ID,
        null,
      );

      // Only initialize if not already done
      if (!notebookId) {
        const result = await model2Service.initializeModel2();
        localStorageUtils.set(
          STORAGE_KEYS.MODEL2_NOTEBOOK_ID,
          result.notebookId,
        );
        localStorageUtils.set(
          STORAGE_KEYS.MODEL2_CHAT_MODEL_ID,
          result.chatModelId,
        );
        localStorageUtils.set(
          STORAGE_KEYS.MODEL2_EMBEDDING_MODEL_ID,
          result.embeddingModelId,
        );
      }
    } catch (error) {
      console.error("Failed to initialize Model 2:", error);
      addNotification("error", "Failed to initialize Model 2 backend");
    } finally {
      setIsInitializingModel2(false);
    }
  }, [addNotification]);

  useEffect(() => {
    initializeModel2Background();
    refreshUploadedFiles();
  }, [initializeModel2Background, refreshUploadedFiles]);

  const saveRecent = (upload: RecentUpload) => {
    const current = localStorageUtils.get<RecentUpload[]>(
      STORAGE_KEYS.RECENT_UPLOADS,
      [],
    );
    const updated = [
      upload,
      ...current.filter((u) => u.id !== upload.id),
    ].slice(0, 5);
    localStorageUtils.set(STORAGE_KEYS.RECENT_UPLOADS, updated);
  };

  const handleFileSelect = (fileList: FileList | null) => {
    if (!fileList) return;
    const newFiles = Array.from(fileList).map((file) => ({
      file,
      id: Date.now() + Math.random().toString(),
      progress: 0,
      status: "pending" as const,
    }));
    setFiles((p) => [...p, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Drag & Drop Handlers
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removeFile = (id: string) => {
    setFiles((p) => p.filter((f) => f.id !== id));
  };

  const resolveAbsoluteDownloadUrl = (url: string | null): string | null => {
    if (!url) return null;
    try {
      return new URL(url, model2Service.API_BASE_URL).toString();
    } catch {
      return null;
    }
  };

  const handleDownloadUploadedFile = async (file: UploadedFileItem) => {
    let downloadUrl = resolveAbsoluteDownloadUrl(file.downloadUrl);

    if (!downloadUrl) {
      try {
        const source = await model2Service.getSource(file.id);
        downloadUrl = resolveAbsoluteDownloadUrl(
          source.asset?.url || source.asset?.file_path || null,
        );
      } catch (error) {
        console.warn("Failed to fetch latest source for download:", error);
      }
    }

    if (!downloadUrl) {
      addNotification("error", "Download link is not available yet");
      return;
    }

    try {
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error("Download fetch failed");

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = file.name || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      addNotification("success", "Download started");
    } catch (error) {
      window.open(downloadUrl, "_blank", "noopener,noreferrer");
      addNotification(
        "info",
        "Opened file in a new tab. Save it from your browser if needed.",
      );
    }
  };

  const handleDeleteUploadedFile = async (file: UploadedFileItem) => {
    try {
      setDeletingSourceId(file.id);
      let analyticsFilename = file.name;
      try {
        const latestSource = await model2Service.getSource(file.id);
        analyticsFilename =
          latestSource.title ||
          getFilenameFromPath(
            latestSource.asset?.file_path || latestSource.asset?.url || file.name,
          ) ||
          file.name;
      } catch (error) {
        console.warn("Failed to resolve latest source name for analytics delete:", error);
      }

      const deletionResults = await Promise.allSettled([
        model2Service.deleteSource(file.id),
        deleteAnalyticsFileByName(analyticsFilename),
      ]);
      const sourceDelete = deletionResults[0];
      const analyticsDelete = deletionResults[1];

      if (sourceDelete.status === "rejected") {
        throw sourceDelete.reason;
      }

      const updatedSourceIds = localStorageUtils
        .get<string[]>(STORAGE_KEYS.MODEL2_SOURCE_IDS, [])
        .filter((id) => id !== file.id);
      localStorageUtils.set(STORAGE_KEYS.MODEL2_SOURCE_IDS, updatedSourceIds);

      const updatedRecent = localStorageUtils
        .get<RecentUpload[]>(STORAGE_KEYS.RECENT_UPLOADS, [])
        .filter((entry) => entry.sourceId !== file.id);
      localStorageUtils.set(STORAGE_KEYS.RECENT_UPLOADS, updatedRecent);

      await refreshUploadedFiles();
      if (analyticsDelete.status === "fulfilled") {
        addNotification(
          "success",
          "File deleted from notebook sources and analytics database",
        );
      } else {
        addNotification(
          "info",
          "Source deleted, but analytics data delete could not be confirmed",
        );
      }
    } catch (error) {
      console.error("Failed to delete source:", error);
      addNotification("error", "Failed to delete uploaded source");
    } finally {
      setDeletingSourceId(null);
    }
  };

  // const handleUpload = async (fileId: string, isRetry = false) => {
  //   const fileObj = files.find((f) => f.id === fileId);
  //   if (!fileObj) return;

  //   // Reset status to uploading
  //   setFiles((p) =>
  //     p.map((f) =>
  //       f.id === fileId
  //         ? { ...f, status: "uploading", progress: 0, error: undefined }
  //         : f,
  //     ),
  //   );

  //   const formData = new FormData();
  //   formData.append("files", fileObj.file);

  //   // --- SIMULATED PROGRESS ---
  //   // Ensures the user sees visual activity (0 -> 90%) while the network request is pending
  //   const progressInterval = setInterval(() => {
  //     setFiles((prev) => {
  //       return prev.map((f) => {
  //         if (f.id === fileId && f.status === "uploading") {
  //           // Increment progress but cap at 90% until real completion
  //           const nextProgress = f.progress + Math.random() * 10;
  //           return { ...f, progress: nextProgress > 90 ? 90 : nextProgress };
  //         }
  //         return f;
  //       });
  //     });
  //   }, 400);

  //   try {
  //     const xhr = new XMLHttpRequest();

  //     await new Promise((resolve, reject) => {
  //       // Note: Real upload progress could be monitored here (xhr.upload.onprogress),
  //       // but we rely on the simulation interval for smoother UX on small files/fast networks.

  //       xhr.addEventListener("load", () => {
  //         if (xhr.status >= 200 && xhr.status < 300) {
  //           // Try to parse JSON, or resolve if empty
  //           try {
  //             resolve(JSON.parse(xhr.response));
  //           } catch (e) {
  //             resolve(xhr.response);
  //           }
  //         } else {
  //           reject(new Error(xhr.statusText || "Upload failed"));
  //         }
  //       });

  //       xhr.addEventListener("error", () => reject(new Error("Network Error")));
  //       xhr.open("POST", API_UPLOAD_URL);
  //       xhr.setRequestHeader("Authorization", `Bearer ${AUTH_TOKEN}`);
  //       xhr.send(formData);
  //     });

  //     // --- COMPLETION FOR MODEL 1 ---
  //     clearInterval(progressInterval);

  //     setFiles((prev) => prev.filter((f) => f.id !== fileId));

  //     let sourceId: string | undefined;

  //     // --- UPLOAD TO MODEL 2 ---
  //     try {
  //       const notebookId = localStorageUtils.get<string | null>(
  //         STORAGE_KEYS.MODEL2_NOTEBOOK_ID,
  //         null,
  //       );

  //       if (notebookId) {
  //         // Fetch transformations and filter for "Key Insights"
  //         const transformations = await model2Service.getTransformations();
  //         const keyInsightsTransformation = transformations.find(
  //           (t: any) => t.name === "Key Insights",
  //         );
  //         const transformationIds = keyInsightsTransformation
  //           ? [keyInsightsTransformation.id]
  //           : [];

  //         const uploadResponse = await model2Service.uploadSource(
  //           fileObj.file,
  //           [notebookId],
  //           transformationIds,
  //           true,
  //           true,
  //         );

  //         // Store the ACTUAL SOURCE ID from the response (not file ID)
  //         const currentSourceIds = localStorageUtils.get<string[]>(
  //           STORAGE_KEYS.MODEL2_SOURCE_IDS,
  //           [],
  //         );
  //         const updatedSourceIds = Array.from(
  //           new Set([...currentSourceIds, uploadResponse.id]),
  //         );
  //         localStorageUtils.set(
  //           STORAGE_KEYS.MODEL2_SOURCE_IDS,
  //           updatedSourceIds,
  //         );
  //         sourceId = uploadResponse.id;
  //       }
  //     } catch (model2Error) {
  //       console.warn("Model 2 source upload warning:", model2Error);
  //       // Don't fail the entire upload if Model 2 fails
  //     }

  //     addNotification("success", `${fileObj.file.name} uploaded successfully`);

  //     saveRecent({
  //       id: fileId,
  //       name: fileObj.file.name,
  //       size: fileObj.file.size,
  //       timestamp: new Date().toISOString(),
  //       status: "success",
  //       sourceId,
  //     });

  //     if (sourceId) {
  //       refreshUploadedFiles();
  //     }
  //   } catch (error: any) {
  //     clearInterval(progressInterval);

  //     setFiles((prev) =>
  //       prev.map((f) =>
  //         f.id === fileId
  //           ? {
  //               ...f,
  //               status: "error",
  //               progress: 0,
  //               error: error.message || "Upload failed",
  //             }
  //           : f,
  //       ),
  //     );

  //     if (!isRetry) {
  //       addNotification("error", `Failed to upload ${fileObj.file.name}`);
  //     }

  //     saveRecent({
  //       id: fileId,
  //       name: fileObj.file.name,
  //       size: fileObj.file.size,
  //       timestamp: new Date().toISOString(),
  //       status: "error",
  //     });
  //   }
  // };

  const handleUpload = async (fileId: string, isRetry = false) => {
    const fileObj = files.find((f) => f.id === fileId);
    if (!fileObj) return;

    // Reset status to uploading
    setFiles((p) =>
      p.map((f) =>
        f.id === fileId
          ? { ...f, status: "uploading", progress: 0, error: undefined }
          : f,
      ),
    );

    // --- SIMULATED PROGRESS (0 → 90%) ---
    const progressInterval = setInterval(() => {
      setFiles((prev) =>
        prev.map((f) => {
          if (f.id === fileId && f.status === "uploading") {
            const nextProgress = f.progress + Math.random() * 10;
            return {
              ...f,
              progress: nextProgress > 90 ? 90 : nextProgress,
            };
          }
          return f;
        }),
      );
    }, 400);

    try {
      // ==============================
      // 🚫 MODEL 1 UPLOAD DISABLED
      // ==============================
      // Instead of calling:
      // xhr.open("POST", API_UPLOAD_URL);
      // we simulate a network delay

      await new Promise((resolve) => setTimeout(resolve, 2000));

      // --- COMPLETE PROGRESS TO 100% ---
      clearInterval(progressInterval);

      setFiles((prev) =>
        prev.map((f) => (f.id === fileId ? { ...f, progress: 100 } : f)),
      );

      // Small delay so user sees 100%
      await new Promise((resolve) => setTimeout(resolve, 400));

      // Remove from uploading list
      setFiles((prev) => prev.filter((f) => f.id !== fileId));

      let sourceId: string | undefined;

      // ==============================
      // MODEL 2 UPLOAD (UNCHANGED)
      // ==============================
      try {
        const notebookId = localStorageUtils.get<string | null>(
          STORAGE_KEYS.MODEL2_NOTEBOOK_ID,
          null,
        );

        if (notebookId) {
          const transformations = await model2Service.getTransformations();

          const keyInsightsTransformation = transformations.find(
            (t: any) => t.name === "Key Insights",
          );

          const transformationIds = keyInsightsTransformation
            ? [keyInsightsTransformation.id]
            : [];

          const uploadResponse = await model2Service.uploadSource(
            fileObj.file,
            [notebookId],
            transformationIds,
            true,
            true,
          );

          const currentSourceIds = localStorageUtils.get<string[]>(
            STORAGE_KEYS.MODEL2_SOURCE_IDS,
            [],
          );

          const updatedSourceIds = Array.from(
            new Set([...currentSourceIds, uploadResponse.id]),
          );

          localStorageUtils.set(
            STORAGE_KEYS.MODEL2_SOURCE_IDS,
            updatedSourceIds,
          );

          sourceId = uploadResponse.id;
        }
      } catch (model2Error) {
        console.warn("Model 2 source upload warning:", model2Error);
        // Do not fail overall upload
      }

      addNotification("success", `${fileObj.file.name} uploaded successfully`);

      saveRecent({
        id: fileId,
        name: fileObj.file.name,
        size: fileObj.file.size,
        timestamp: new Date().toISOString(),
        status: "success",
        sourceId,
      });

      if (sourceId) {
        await refreshUploadedFiles({ silent: true });
        void waitForSourceCompletion(sourceId);
      }
    } catch (error: any) {
      clearInterval(progressInterval);

      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileId
            ? {
                ...f,
                status: "error",
                progress: 0,
                error: error?.message || "Upload failed",
              }
            : f,
        ),
      );

      if (!isRetry) {
        addNotification("error", `Failed to upload ${fileObj.file.name}`);
      }

      saveRecent({
        id: fileId,
        name: fileObj.file.name,
        size: fileObj.file.size,
        timestamp: new Date().toISOString(),
        status: "error",
      });
    }
  };


  const uploadAll = async () => {
    setIsUploadingAll(true);
    const pending = files.filter(
      (x) => x.status === "pending" || x.status === "error",
    );
    for (const f of pending) {
      await handleUpload(f.id);
    }
    setIsUploadingAll(false);
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 p-4 lg:p-8 animate-in fade-in duration-500 pb-24">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
            Data Ingestion
          </h1>
          <p className="text-slate-500 mt-1 flex items-center gap-2 text-sm">
            <UploadCloud size={14} /> Secure Cloud Upload
          </p>
        </div>
        <div className="flex gap-3 w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            className="flex-1 sm:flex-none border-dashed border-indigo-300 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
          >
            <Plus size={16} className="mr-2" /> Add Files
          </Button>
          <Button
            onClick={uploadAll}
            disabled={files.length === 0 || isUploadingAll}
            className="flex-1 sm:flex-none shadow-lg shadow-indigo-500/20 bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {isUploadingAll ? (
              <Loader2 className="animate-spin mr-2" size={16} />
            ) : (
              <Play size={16} className="mr-2" />
            )}
            {isUploadingAll ? "Processing..." : "Process Batch"}
          </Button>
        </div>
      </div>

      <div className="space-y-8">
        <div className="space-y-6">
          {/* Drop Zone */}
          <div
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              "relative group border-2 border-dashed rounded-2xl p-10 transition-all duration-300 ease-in-out flex flex-col items-center justify-center text-center cursor-pointer overflow-hidden",
              isDragging
                ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10 scale-[1.01] shadow-xl shadow-indigo-500/10 ring-4 ring-indigo-500/10"
                : "border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-indigo-400 hover:bg-slate-50 dark:hover:bg-slate-800/50",
            )}
          >
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-indigo-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".xlsx,.xls,.csv,.json"
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files)}
            />

            <div
              className={cn(
                "w-20 h-20 rounded-full flex items-center justify-center mb-6 transition-all duration-500",
                isDragging
                  ? "bg-indigo-100 dark:bg-indigo-500/20 scale-110"
                  : "bg-slate-100 dark:bg-slate-800 group-hover:scale-110 group-hover:bg-indigo-50 dark:group-hover:bg-slate-700",
              )}
            >
              <UploadCloud
                className={cn(
                  "w-10 h-10 transition-colors",
                  isDragging
                    ? "text-indigo-600 dark:text-indigo-400"
                    : "text-slate-400 group-hover:text-indigo-500",
                )}
              />
            </div>

            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
              {isDragging
                ? "Drop files now"
                : "Click to upload or drag and drop"}
            </h3>
            <p className="text-slate-500 text-sm max-w-sm mx-auto">
              Support for Excel (.xlsx, .xls), CSV, and JSON files up to 50MB.
            </p>
          </div>

          {/* Staged Files List */}
          {files.length > 0 && (
            <div className="space-y-4 animate-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-center justify-between px-1">
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider">
                  Queue ({files.length})
                </h3>
                <button
                  onClick={() => setFiles([])}
                  className="text-xs text-red-500 hover:text-red-600 hover:underline transition-colors"
                >
                  Clear All
                </button>
              </div>

              <div className="space-y-3">
                {files.map((f) => (
                  <div
                    key={f.id}
                    className="relative group flex items-center gap-4 p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm transition-all hover:shadow-md hover:border-indigo-200 dark:hover:border-indigo-900"
                  >
                    <div
                      className={cn(
                        "absolute left-0 top-0 bottom-0 w-1 rounded-l-xl transition-colors",
                        f.status === "success"
                          ? "bg-emerald-500"
                          : f.status === "error"
                            ? "bg-red-500"
                            : f.status === "uploading"
                              ? "bg-indigo-500"
                              : "bg-slate-200 dark:bg-slate-700",
                      )}
                    />

                    <div className="p-2.5 bg-slate-50 dark:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-300">
                      <FileSpreadsheet size={20} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center mb-1">
                        <p className="font-semibold text-sm text-slate-900 dark:text-white truncate pr-4">
                          {f.file.name}
                        </p>
                        <span className="text-[10px] font-mono text-slate-400 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                          {(f.file.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </div>

                      <div className="flex items-center gap-2 h-4">
                        {f.status === "uploading" ? (
                          <div className="w-full flex items-center gap-3">
                            <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-indigo-500 rounded-full transition-all duration-300 ease-out"
                                style={{ width: `${f.progress}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400 w-8 text-right">
                              {Math.round(f.progress)}%
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-xs">
                            {f.status === "success" && (
                              <span className="text-emerald-600 flex items-center gap-1 font-medium">
                                <CheckCircle2 size={12} /> Completed
                              </span>
                            )}
                            {f.status === "error" && (
                              <span className="text-red-600 flex items-center gap-1 font-medium">
                                <XCircle size={12} /> {f.error}
                              </span>
                            )}
                            {f.status === "pending" && (
                              <span className="text-slate-400 flex items-center gap-1">
                                <Clock size={12} /> Waiting...
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      {f.status === "error" && (
                        <button
                          onClick={() => handleUpload(f.id, true)}
                          className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                          title="Retry"
                        >
                          <RotateCcw size={16} />
                        </button>
                      )}
                      {f.status === "pending" && (
                        <button
                          onClick={() => handleUpload(f.id)}
                          className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                          title="Upload"
                        >
                          <Play size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => removeFile(f.id)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Remove"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Uploaded Files */}
          <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
            <div className="p-5 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex justify-between items-center">
              <h4 className="font-bold text-xs text-slate-500 uppercase tracking-wider">
                Uploaded Files
              </h4>
              <span className="text-[10px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2 py-1 rounded-full text-slate-500 font-medium">
                {isInitializingModel2 || isLoadingUploadedFiles
                  ? "Syncing..."
                  : `${uploadedFiles.length} files`}
              </span>
            </div>
            <div className="p-3 space-y-2">
              {isLoadingUploadedFiles ? (
                <div className="h-28 flex flex-col items-center justify-center text-center text-slate-500">
                  <Loader2 size={18} className="animate-spin mb-2" />
                  <p className="text-xs font-medium">Loading uploaded files...</p>
                </div>
              ) : uploadedFiles.length > 0 ? (
                uploadedFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-950/40"
                  >
                    <div className="p-2 bg-white dark:bg-slate-800 rounded-lg text-slate-600 dark:text-slate-300">
                      <FileText size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                        {file.name}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className={cn(
                            "text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide",
                            file.status === "completed"
                              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                              : file.status === "error"
                                ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400"
                                : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
                          )}
                        >
                          {file.status}
                        </span>
                        {typeof file.size === "number" && (
                          <span className="text-[10px] text-slate-500">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDownloadUploadedFile(file)}
                        disabled={!file.fileAvailable}
                        className="p-2 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Download file"
                      >
                        <Download size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteUploadedFile(file)}
                        disabled={deletingSourceId === file.id}
                        className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        title="Delete from notebook sources"
                      >
                        {deletingSourceId === file.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="h-28 flex flex-col items-center justify-center text-center text-slate-400">
                  <FileText size={18} className="opacity-30 mb-2" />
                  <p className="text-xs font-medium">No uploaded files yet</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
